'use client';

import { c as _c } from "react/compiler-runtime";
import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { useLocale } from 'next-intl';
import { AmplienceVideo } from '@/types/amplience';
import { useIdleFlag } from '@/hooks/useIdleFlag';
import { getVideoTrackProps } from '@/utility/getVideoTrackProps';
import { getVideoUrl } from '@/utility/getVideoUrl';
import { DescriptionTrack, wireDescriptionAnnouncer } from '@/utility/videoDescriptions';
type AmplienceVideoPlayerProps = {
  video: AmplienceVideo | undefined;
  formats: {
    webm: string;
    mp4: string;
  };
  /** Announced by screen readers. Optional: content saved before the field existed has none. */
  altText?: string;
  transcriptUrl?: string;
  poster?: string;
  /** Rendered instead of the video until the browser signals idle. */
  fallback: ReactNode;
  className?: string;
  style?: CSSProperties;
  controls?: boolean;
  controlsList?: string;
};
export default function AmplienceVideoPlayer(t0) {
  const $ = _c(28);
  const {
    video,
    formats,
    altText,
    transcriptUrl,
    poster,
    fallback,
    className,
    style,
    controls,
    controlsList
  } = t0;
  const canLoadVideo = useIdleFlag();
  const locale = useLocale();
  let t1;
  if ($[0] !== locale || $[1] !== transcriptUrl) {
    t1 = getVideoTrackProps(transcriptUrl, locale);
    $[0] = locale;
    $[1] = transcriptUrl;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const trackProps = t1;
  const videoRef = useRef(null);
  const trackRef = useRef(null);
  const [description, setDescription] = useState("");
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => wireDescriptionAnnouncer(videoRef.current, trackRef.current?.track as DescriptionTrack | undefined, setDescription);
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = trackProps?.src;
  let t4;
  if ($[4] !== canLoadVideo || $[5] !== t3) {
    t4 = [canLoadVideo, t3];
    $[4] = canLoadVideo;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useEffect(t2, t4);
  if (!canLoadVideo) {
    return fallback;
  }
  const t5 = trackProps ? "anonymous" : undefined;
  let t6;
  if ($[7] !== formats || $[8] !== video) {
    t6 = video && <><source src={getVideoUrl(video, formats.webm)} type="video/webm" /><source src={getVideoUrl(video, formats.mp4)} type="video/mp4" /></>;
    $[7] = formats;
    $[8] = video;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== trackProps) {
    t7 = trackProps && <track ref={trackRef} {...trackProps} />;
    $[10] = trackProps;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== altText || $[13] !== className || $[14] !== controls || $[15] !== controlsList || $[16] !== poster || $[17] !== style || $[18] !== t5 || $[19] !== t6 || $[20] !== t7) {
    t8 = <video ref={videoRef} autoPlay={true} playsInline={true} muted={true} loop={true} aria-label={altText} controls={controls} controlsList={controlsList} crossOrigin={t5} className={className} style={style} poster={poster}>{t6}{t7}</video>;
    $[12] = altText;
    $[13] = className;
    $[14] = controls;
    $[15] = controlsList;
    $[16] = poster;
    $[17] = style;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== description || $[23] !== trackProps) {
    t9 = trackProps && <span className="sr-only" aria-live="polite">{description}</span>;
    $[22] = description;
    $[23] = trackProps;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t8 || $[26] !== t9) {
    t10 = <>{t8}{t9}</>;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}